import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import PdfViewer from '../components/PdfViewer';
import './VisualizarResumo.css';

const VisualizarResumo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [resumo, setResumo] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fullscreenSupported, setFullscreenSupported] = useState(false);
  const [componentError, setComponentError] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Global error handler for this component
  useEffect(() => {
    const handleError = (event) => {
      console.error('Error in VisualizarResumo:', event.error);
      setComponentError(true);
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Check fullscreen support on component mount
  useEffect(() => {
    const checkFullscreenSupport = () => {
      try {
        // Check if fullscreen API is available and enabled
        const isSupported = !!(
          document.fullscreenEnabled ||
          document.webkitFullscreenEnabled ||
          document.mozFullScreenEnabled ||
          document.msFullscreenEnabled
        );
        setFullscreenSupported(isSupported);
        console.log('Fullscreen API supported:', isSupported);
      } catch (error) {
        console.log('Error checking fullscreen support:', error);
        setFullscreenSupported(false);
      }
    };

    checkFullscreenSupport();
  }, []);

  useEffect(() => {
    fetchResumo();
  }, [id]);

  useEffect(() => {
    // Reset error when URL changes
    setPdfError(false);
  }, [pdfUrl]);

  // Zoom handlers
  const zoomIn = () => {
    try {
      setZoomLevel(prev => Math.min(prev + 25, 200)); // Max 200%
    } catch (error) {
      console.error('Error zooming in:', error);
    }
  };

  const zoomOut = () => {
    try {
      setZoomLevel(prev => Math.max(prev - 25, 50)); // Min 50%
    } catch (error) {
      console.error('Error zooming out:', error);
    }
  };

  const resetZoom = () => {
    try {
      setZoomLevel(100);
    } catch (error) {
      console.error('Error resetting zoom:', error);
    }
  };

  // Fullscreen handlers with robust error handling
  const toggleFullscreen = async () => {
    // Don't proceed if fullscreen is not supported
    if (!fullscreenSupported) {
      console.log('Fullscreen not supported on this browser');
      toast.info('Seu navegador não suporta modo de tela cheia para PDFs');
      return;
    }

    try {
      const container = document.querySelector('.pdf-viewer-container');
      
      if (!container) {
        console.error('PDF container not found');
        return;
      }
      
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (!isCurrentlyFullscreen) {
        // Enter fullscreen
        try {
          if (container.requestFullscreen) {
            await container.requestFullscreen();
          } else if (container.webkitRequestFullscreen) {
            await container.webkitRequestFullscreen(); // Safari
          } else if (container.mozRequestFullScreen) {
            await container.mozRequestFullScreen(); // Firefox
          } else if (container.msRequestFullscreen) {
            await container.msRequestFullscreen(); // IE11
          } else {
            throw new Error('No fullscreen method available');
          }
          setIsFullscreen(true);
        } catch (error) {
          console.error('Error entering fullscreen:', error);
          // Don't show error to user - might be user cancellation
          setIsFullscreen(false);
        }
      } else {
        // Exit fullscreen
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            await document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
          }
          setIsFullscreen(false);
          // Reset zoom when exiting fullscreen
          setZoomLevel(100);
        } catch (error) {
          console.error('Error exiting fullscreen:', error);
          setIsFullscreen(false);
          setZoomLevel(100);
        }
      }
    } catch (error) {
      console.error('Unexpected error in toggleFullscreen:', error);
      // Silently handle - don't show error overlay to user
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    // Listen for fullscreen changes (including ESC key)
    const handleFullscreenChange = () => {
      try {
        const isCurrentlyFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );
        setIsFullscreen(isCurrentlyFullscreen);
        
        // Reset zoom if exiting fullscreen
        if (!isCurrentlyFullscreen && zoomLevel !== 100) {
          setZoomLevel(100);
        }
      } catch (error) {
        console.error('Error handling fullscreen change:', error);
        setIsFullscreen(false);
      }
    };

    try {
      document.addEventListener('fullscreenchange', handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', handleFullscreenChange);
      document.addEventListener('msfullscreenchange', handleFullscreenChange);

      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
        document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      };
    } catch (error) {
      console.error('Error setting up fullscreen listeners:', error);
    }
  }, [zoomLevel]);

  const fetchResumo = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch resumo metadata
      const resumoResponse = await fetch(`${backendUrl}/api/resumos/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!resumoResponse.ok) {
        toast.error('Resumo não encontrado');
        navigate('/usuario/resumos');
        return;
      }

      const resumoData = await resumoResponse.json();
      setResumo(resumoData);

      // If resumo has PDF, fetch signed URL
      if (resumoData.pdfUrl) {
        setLoadingPdf(true);
        try {
          const pdfResponse = await fetch(`${backendUrl}/api/usuario/resumos/${id}/pdf`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (pdfResponse.ok) {
            const pdfData = await pdfResponse.json();
            setPdfUrl(pdfData.url);
          } else {
            toast.error('Erro ao carregar PDF');
          }
        } catch (error) {
          console.error('Erro ao buscar URL do PDF:', error);
          toast.error('Erro ao carregar PDF');
        } finally {
          setLoadingPdf(false);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar resumo:', error);
      toast.error('Erro ao carregar resumo');
      navigate('/usuario/resumos');
    } finally {
      setLoading(false);
    }
  };

  // Show error UI instead of red overlay
  if (componentError) {
    return (
      <div className="visualizar-page">
        <div className="visualizar-container">
          <div className="visualizar-header">
            <Button
              variant="ghost"
              onClick={() => navigate('/usuario/resumos')}
              className="back-button-viz"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para resumos
            </Button>
          </div>
          <div className="pdf-viewer-container">
            <div className="pdf-error-state">
              <div className="error-icon">⚠️</div>
              <h3>Não foi possível carregar o PDF</h3>
              <p>Ocorreu um erro ao tentar exibir o documento. Por favor, tente recarregar a página ou entre em contato com o suporte.</p>
              <div className="error-actions">
                <button onClick={() => window.location.reload()} className="btn-reload">
                  Recarregar Página
                </button>
                <button onClick={() => navigate('/usuario/resumos')} className="btn-back">
                  Voltar para Resumos
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="visualizar-page">
        <div className="visualizar-container">
          <p className="loading-text">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!resumo) {
    return null;
  }

  return (
    <div className="visualizar-page">
      {/* Header */}
      <div className="visualizar-header">
        <div className="header-content-viz">
          <Button
            variant="ghost"
            onClick={() => navigate('/usuario/resumos')}
            className="back-button-viz"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para resumos
          </Button>
          <div className="header-info">
            <h1 className="visualizar-title">{resumo.titulo}</h1>
            <span className="visualizar-especialidade">
              {/* Aqui você pode buscar o nome da especialidade se necessário */}
              Especialidade
            </span>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="pdf-viewer-container">
        {/* Fullscreen Toggle Button - only show if supported */}
        {pdfUrl && !loadingPdf && fullscreenSupported && (
          <button
            onClick={toggleFullscreen}
            className="fullscreen-toggle-btn"
            title={isFullscreen ? "Fechar visualização (ESC)" : "Expandir visualização"}
            disabled={!fullscreenSupported}
          >
            {isFullscreen ? (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="btn-text">Fechar visualização</span>
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
                <span className="btn-text">Expandir visualização</span>
              </>
            )}
          </button>
        )}

        {loadingPdf ? (
          <div className="pdf-placeholder">
            <div className="placeholder-content">
              <svg className="placeholder-icon animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <h3 className="placeholder-title">Carregando PDF...</h3>
              <p className="placeholder-text">Aguarde enquanto buscamos o arquivo.</p>
            </div>
          </div>
        ) : pdfUrl ? (
          <>
            <div 
              className="pdf-wrapper"
              style={{
                transform: isFullscreen ? `scale(${zoomLevel / 100})` : 'scale(1)',
                transformOrigin: 'center center',
                transition: 'transform 0.3s ease'
              }}
            >
              <PdfViewer
                url={pdfUrl}
                title={resumo.titulo}
                className="pdf-iframe"
              />
            </div>
            
            {/* Zoom controls - only in fullscreen */}
            {isFullscreen && (
              <div className="zoom-controls">
                <button
                  onClick={zoomOut}
                  className="zoom-btn"
                  disabled={zoomLevel <= 50}
                  title="Diminuir zoom (25%)"
                >
                  <svg className="zoom-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                
                <span className="zoom-level">{zoomLevel}%</span>
                
                <button
                  onClick={zoomIn}
                  className="zoom-btn"
                  disabled={zoomLevel >= 200}
                  title="Aumentar zoom (25%)"
                >
                  <svg className="zoom-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                
                <button
                  onClick={resetZoom}
                  className="zoom-btn zoom-reset"
                  title="Resetar zoom para 100%"
                >
                  100%
                </button>
              </div>
            )}
            
            {/* Help message - only shown when error AND not in fullscreen */}
            {pdfError && !isFullscreen && (
              <div className="pdf-help-message">
                <div className="help-icon">⚠️</div>
                <div className="help-content">
                  <h4 className="help-title">Não consegue visualizar o PDF?</h4>
                  <ul className="help-list">
                    <li>Tente desativar o bloqueador de anúncios ou VPN neste site</li>
                    <li>Verifique se seu navegador permite exibir PDFs</li>
                  </ul>
                </div>
              </div>
            )}
          </>
        ) : resumo.pdfUrl ? (
          <div className="pdf-placeholder">
            <div className="placeholder-content">
              <svg className="placeholder-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="placeholder-title">Erro ao carregar PDF</h3>
              <p className="placeholder-text">
                Não foi possível carregar o arquivo. Tente novamente mais tarde.
              </p>
            </div>
          </div>
        ) : (
          <div className="pdf-placeholder">
            <div className="placeholder-content">
              <svg className="placeholder-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="placeholder-title">PDF em breve</h3>
              <p className="placeholder-text">
                Este resumo está sendo preparado e em breve estará disponível.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="info-footer">
        <div className="info-content">
          <div className="info-section">
            <h3 className="info-title">Descrição</h3>
            <p className="info-text">{resumo.descricaoCurta}</p>
          </div>
          {resumo.palavrasChave && resumo.palavrasChave.length > 0 && (
            <div className="info-section">
              <h3 className="info-title">Palavras-chave</h3>
              <div className="keywords-list">
                {resumo.palavrasChave.map((palavra, idx) => (
                  <span key={idx} className="keyword-badge">
                    {palavra}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualizarResumo;
